import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { MessageSquare, RefreshCw, Check, X, Loader2, AlertTriangle, Inbox } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  suggested: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  awaiting_approval: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  replied: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  escalated: 'bg-red-500/15 text-red-300 border-red-500/30',
  ignored: 'bg-zinc-700/40 text-zinc-400 border-zinc-600/40',
  none: 'bg-zinc-700/40 text-zinc-300 border-zinc-600/40',
};
const SENTIMENT = { positive: 'text-emerald-400', negative: 'text-red-400', neutral: 'text-zinc-400' };

export default function EngagementInboxPage() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [scope, setScope] = useState('user');
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(null);
  const [edits, setEdits] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/social/engagement/inbox?account_scope=${scope}`, { headers });
      setData(r.data);
    } catch { /* transient */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, token]);

  useEffect(() => { load(); }, [load]);

  const ingest = async () => {
    setBusy('ingest');
    try {
      const r = await axios.post(`${API}/api/social/engagement/ingest?account_scope=${scope}`, {}, { headers });
      const ing = r.data.ingestion;
      toast.success(`Checked ${ing.posts_checked} posts — ${ing.ingested} new comments`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ingestion failed'); }
    finally { setBusy(null); }
  };

  const approve = async (c) => {
    setBusy(c.id);
    try {
      const r = await axios.post(`${API}/api/social/engagement/${c.id}/approve?account_scope=${scope}`,
        { reply: edits[c.id] ?? c.suggested_reply }, { headers });
      if (r.data.success) toast.success('Reply posted'); else toast.error(r.data.error || 'Reply failed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approval failed'); }
    finally { setBusy(null); }
  };

  const ignore = async (c) => {
    setBusy(c.id);
    try { await axios.post(`${API}/api/social/engagement/${c.id}/ignore?account_scope=${scope}`, {}, { headers }); load(); }
    catch { toast.error('Failed'); } finally { setBusy(null); }
  };

  const comments = (data?.comments || []).filter(c => filter === 'all' || c.reply_status === filter);
  const s = data?.summary;

  return (
    <AppShell>
      <div className="p-5 sm:p-7 lg:p-10 max-w-5xl mx-auto" data-testid="engagement-inbox">
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <MessageSquare className="w-6 h-6 text-orange-400" />
          <h1 className="text-2xl font-bold text-white">Engagement Inbox</h1>
          {isNaN(0) ? null : s && (
            <span className="text-xs text-zinc-400">{s.comments_30d} comments · {s.pending_replies} pending · {s.escalated} escalated</span>
          )}
          <div className="flex-1" />
          {user?.is_founder && (
            <select value={scope} onChange={e => setScope(e.target.value)} data-testid="engagement-scope"
              className="bg-zinc-800 border border-orange-500/40 text-orange-300 text-xs rounded-lg px-2 py-1.5">
              <option value="user">My accounts</option>
              <option value="system">Delphi AI (system org)</option>
            </select>
          )}
          <select value={filter} onChange={e => setFilter(e.target.value)} data-testid="engagement-filter"
            className="bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs rounded-lg px-2 py-1.5">
            {['all', 'awaiting_approval', 'suggested', 'escalated', 'replied', 'ignored'].map(f =>
              <option key={f} value={f}>{f.replace('_', ' ')}</option>)}
          </select>
          <Button size="sm" onClick={ingest} disabled={busy === 'ingest'} data-testid="engagement-ingest-btn"
            className="bg-orange-600 hover:bg-orange-500">
            {busy === 'ingest' ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1" /> Check comments</>}
          </Button>
        </div>

        {comments.length === 0 ? (
          <div className="text-center py-16 text-zinc-500" data-testid="engagement-empty">
            <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No comments yet. Once your posts are live, Delphi pulls comments here and drafts replies for you.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {comments.map(c => (
              <div key={c.id} className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4" data-testid={`engagement-comment-${c.id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                  <span className="text-xs font-semibold text-white">{c.author_name}</span>
                  <span className="text-[10px] text-zinc-500 capitalize">{c.platform}</span>
                  <span className={`text-[10px] capitalize ${SENTIMENT[c.sentiment] || 'text-zinc-400'}`}>{c.sentiment}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${STATUS_BADGE[c.reply_status]}`}
                        data-testid={`engagement-status-${c.id}`}>
                    {c.reply_status.replace('_', ' ')}
                  </span>
                  {c.moderation !== 'ok' && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-300 border border-red-500/20 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> {c.moderation}
                    </span>
                  )}
                </div>
                <p className="text-sm text-zinc-300 mb-1">{c.text}</p>
                <p className="text-[10px] text-zinc-600 mb-2">on "{c.post_title}"</p>
                {c.last_error && <p className="text-xs text-red-400 mb-1">{c.last_error}</p>}
                {['awaiting_approval', 'suggested', 'escalated'].includes(c.reply_status) && (
                  <div className="space-y-2">
                    <textarea
                      defaultValue={c.suggested_reply || ''}
                      onChange={e => setEdits(p => ({ ...p, [c.id]: e.target.value }))}
                      data-testid={`engagement-reply-input-${c.id}`}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-white"
                      rows={2} placeholder="Write a reply…" />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => approve(c)} disabled={busy === c.id}
                        className="bg-emerald-600 hover:bg-emerald-500" data-testid={`engagement-approve-${c.id}`}>
                        {busy === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Approve &amp; reply</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => ignore(c)} disabled={busy === c.id}
                        className="border-zinc-700 text-zinc-400" data-testid={`engagement-ignore-${c.id}`}>
                        <X className="w-4 h-4 mr-1" /> Ignore
                      </Button>
                    </div>
                  </div>
                )}
                {c.reply_status === 'replied' && c.suggested_reply && (
                  <p className="text-xs text-emerald-300 border-l-2 border-emerald-500/40 pl-2">↳ {c.suggested_reply}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
